import React, { useState, useEffect } from 'react';
import './Firmware.scss';
import logo from '../../img/avionica.png';
import AppBackground from '../../common/AppBackground';
import InstructionMenu from '../InstructionMenu/InstructionMenu';
import ProgressBar from '../Common/ProgressBar/ProgressBar';
import { downloadWithProgress } from '../../common/downloadWithProgress';

const API_URL = process.env.REACT_APP_API_URL;

const Firmware = ({ embedded = false }) => {
  const [files, setFiles]             = useState([]);
  const [collapsed, setCollapsed]     = useState({});
  const [searchTerm, setSearchTerm]   = useState('');
  const [sort, setSort]               = useState({ key: 'name', dir: 'asc' });
  const [downloading, setDownloading] = useState(null);
  const [downloadPercent, setDownloadPercent] = useState(0); // 0..100, or null when size unknown
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [instrOpen, setInstrOpen]     = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const filesRes = await fetch(`${API_URL}/firmware/images`);
        if (!filesRes.ok) throw new Error(`Files HTTP ${filesRes.status}`);

        const filesData = await filesRes.json();
        setFiles(filesData);
      } catch (err) {
        console.error('Firmware fetch error:', err);
        setError('Failed to load firmware data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Group keys are derived from the images themselves — the distinct recorderModel
  // values present, sorted. New recorder types appear automatically when their first
  // image is uploaded; no config file to keep in sync.
  const recorders = [...new Set(
    files.map(f => f.recorderModel).filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // Matching pocketReader part number per recorder type (from blob metadata). Keyed by
  // lowercased recorder model; takes the first part number seen for that type.
  const partByRecorder = files.reduce((acc, f) => {
    const key = (f.recorderModel || '').toLowerCase();
    if (key && f.partNumber && !acc[key]) acc[key] = f.partNumber;
    return acc;
  }, {});

  const downloadFile = async (filename) => {
    if (downloading === filename) return;
    setDownloading(filename);
    setDownloadPercent(0);
    try {
      const { ok, status } = await downloadWithProgress(
        `${API_URL}/firmware/download/${encodeURIComponent(filename)}`,
        filename,
        { onProgress: setDownloadPercent }
      );
      if (!ok) throw new Error(`HTTP ${status}`);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const toggleCollapse  = (recorder) =>
    setCollapsed(prev => ({ ...prev, [recorder]: !prev[recorder] }));

  const toggleSort = (key) =>
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );

  const sortIndicator = (key) =>
    sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  // Group files by recorderModel — drop files with no recorderModel
  const grouped = recorders.reduce((acc, recorder) => {
    acc[recorder] = files
      .filter(f =>
        f.recorderModel &&
        f.recorderModel.toLowerCase() === recorder.toLowerCase() &&
        f.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice()
      .sort((a, b) => {
        const cmp = String(a.name).localeCompare(String(b.name), undefined, {
          numeric: true, sensitivity: 'base',
        });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    return acc;
  }, {});

  const shell = (
    <div className="firmware-shell">

          <div className="firmware-toolbar">
            <h2 className="firmware-toolbar__heading">Firmware Images</h2>
            <input
              className="firmware-toolbar__search"
              type="text"
              placeholder="Search files…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {loading && <p className="firmware-status">Loading…</p>}
          {error   && <p className="firmware-status firmware-status--error">{error}</p>}

          {!loading && !error && (
            <div className="firmware-list">
            {recorders.map(recorder => {
            const rows        = grouped[recorder];
            const isCollapsed = !!collapsed[recorder];
            const partNumber  = partByRecorder[recorder.toLowerCase()];

            return (
              <div key={recorder} className="firmware-group">
                <button
                  className="firmware-group__header"
                  onClick={() => toggleCollapse(recorder)}
                  type="button"
                >
                  <span className="firmware-group__name">
                    {recorder}
                    {partNumber && (
                      <span className="firmware-group__pn">P/N {partNumber}</span>
                    )}
                  </span>
                  <span className="firmware-group__count">
                    {rows.length} image{rows.length !== 1 ? 's' : ''}
                  </span>
                  <span className="firmware-group__chevron">
                    {isCollapsed ? '▶' : '▼'}
                  </span>
                </button>

                {!isCollapsed && (
                  <div className="firmware-group__table-wrap">
                    <table className="firmware-group__table">
                      <thead>
                        <tr>
                          <th className="sortable" onClick={() => toggleSort('name')}>
                            File{sortIndicator('name')}
                          </th>
                          <th className="firmware-group__date-col">Date</th>
                          <th>Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="firmware-group__empty">
                              No images found.
                            </td>
                          </tr>
                        ) : (
                          rows.map(file => (
                          <tr key={file.name}>
                            <td>{file.name}</td>
                            <td className="firmware-group__date-col">
                              {file.uploadDate
                                ? new Date(file.uploadDate).toLocaleDateString()
                                : '—'}
                            </td>
                            <td>
                              <button
                                className="firmware-dl-btn"
                                onClick={() => downloadFile(file.name)}
                                disabled={downloading === file.name}
                              >
                                {downloading === file.name ? 'Downloading…' : 'Download'}
                              </button>
                              {downloading === file.name && (
                                <div className="download-progress">
                                  <ProgressBar barHeight={6} progressPercentage={downloadPercent} />
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          )}

          {/* Global pocketReader update instructions — collapsible, folded by default */}
          <div className="firmware-group firmware-instructions">
            <button
              className="firmware-group__header"
              onClick={() => setInstrOpen(o => !o)}
              type="button"
            >
              <span className="firmware-group__name">How to update your pocketReader</span>
              <span className="firmware-group__chevron">{instrOpen ? '▼' : '▶'}</span>
            </button>
            {instrOpen && (
              <div className="firmware-instructions__body">
                <InstructionMenu endpoint="getUpdateInstructions" global imageEndpoint="firmware/image" />
              </div>
            )}
          </div>

    </div>
  );

  // Embedded (inside the QR-flow view-frame): render only the shell — the surrounding
  // page already provides the background, logo, and header chrome.
  if (embedded) {
    return <div className="firmware-embedded">{shell}</div>;
  }

  // Standalone (?firmware): full-page chrome.
  return (
    <div className="firmware-page">
      <AppBackground />
      <div className="firmware-page__inner">

        <div className="firmware-page__header">
          <img className="firmware-page__logo" src={logo} alt="Avionica" />
          <h1 className="firmware-page__title">avCloudConnect</h1>
        </div>

        {shell}

      </div>
    </div>
  );
};

export default Firmware;